import { useState, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Link } from '@tanstack/react-router';
import { BellRing, Bell as BellIcon } from 'lucide-react';
import { useFormat } from '@paige/format';
import { useInbox, useMarkRead } from '../api/notification-queries';
import type { Notification } from '../api/types';
import { linkParts } from './link-parts';
import { noticeLook, splitNotice } from './notice-look';
import './bell.css';

/**
 * The bell in the bar (IMPLEMENTATION.md row 41): the inbox's unread count on it, the inbox under
 * it. A row is the way into the thing that asked (the job's exceptions, Billing); opening the
 * inbox marks nothing read, following a row marks that one, "Mark all read" is one press. Never a
 * value from a document: the subject is content-free by contract.
 *
 * The inbox is a dialog that is not modal (the shared Dialog's own library, in the bell's look):
 * opening it moves the keyboard into it, Escape or a press elsewhere closes it, and the focus goes
 * back to the bell.
 */
export function Bell({ tenantId }: { tenantId: string }): ReactNode {
  const inbox = useInbox(tenantId);
  const markRead = useMarkRead(tenantId);
  const [open, setOpen] = useState(false);
  const unread = inbox.data?.unread ?? 0;
  return (
    <RadixDialog.Root open={open} onOpenChange={setOpen} modal={false}>
      <div className="bell">
        <RadixDialog.Trigger
          className="company-mark bell__button"
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <BellIcon size={17} aria-hidden="true" />
          {unread > 0 && <span className="bell__count">{unread > 99 ? '99+' : unread}</span>}
        </RadixDialog.Trigger>
        <RadixDialog.Content
          className="bell__inbox"
          aria-label="Notifications"
          aria-describedby={undefined}
        >
          <RadixDialog.Title className="bell__title">Notifications</RadixDialog.Title>
          <Inbox
            read={readOf(inbox)}
            onRetry={() => void inbox.refetch()}
            onFollow={(n) => {
              if (!n.readAt) markRead.mutate([n.id]);
              setOpen(false);
            }}
            onReadAll={() => markRead.mutate(undefined)}
            onChoose={() => setOpen(false)}
          />
        </RadixDialog.Content>
      </div>
    </RadixDialog.Root>
  );
}

/** Where the inbox's read stands: never "Nothing new" for an inbox that has not been read. */
type InboxRead =
  | { state: 'reading' }
  | { state: 'failed'; retrying: boolean }
  | { state: 'read'; items: Notification[]; unread: number };

function readOf(inbox: ReturnType<typeof useInbox>): InboxRead {
  if (inbox.data) return { state: 'read', items: inbox.data.items, unread: inbox.data.unread };
  if (inbox.isError) return { state: 'failed', retrying: inbox.isFetching };
  return { state: 'reading' };
}

function Inbox({
  read,
  onRetry,
  onFollow,
  onReadAll,
  onChoose,
}: {
  read: InboxRead;
  onRetry: () => void;
  onFollow: (n: Notification) => void;
  onReadAll: () => void;
  /** "Choose what you get": the address asks the profile for "Emails and notices" (`#notices`); the inbox closes. */
  onChoose: () => void;
}): ReactNode {
  return (
    <>
      <header className={`bell__head${read.state === 'failed' ? ' bell__head--said' : ''}`}>
        <span role="status" aria-busy={read.state === 'reading'}>
          {headline(read)}
        </span>
        <HeadAction read={read} onRetry={onRetry} onReadAll={onReadAll} />
      </header>
      {read.state === 'read' &&
        (read.items.length === 0 ? (
          <div className="bell__empty">
            <span className="bell__empty-mark" aria-hidden="true">
              <BellRing size={20} />
            </span>
            <p>When a job needs you, it will ask here.</p>
          </div>
        ) : (
          <InboxList items={read.items} onFollow={onFollow} />
        ))}
      <a className="bell__choose" href="#notices" onClick={onChoose}>
        Choose what you get
      </a>
    </>
  );
}

/** Beside the headline: mark everything read, or ask again for an inbox that did not load. */
function HeadAction(props: {
  read: InboxRead;
  onRetry: () => void;
  onReadAll: () => void;
}): ReactNode {
  const { read } = props;
  if (read.state === 'failed')
    return (
      <button type="button" className="bell__all" disabled={read.retrying} onClick={props.onRetry}>
        {read.retrying ? 'Trying…' : 'Try again'}
      </button>
    );
  if (read.state !== 'read' || read.unread === 0) return null;
  return (
    <button type="button" className="bell__all" onClick={props.onReadAll}>
      Mark all read
    </button>
  );
}

function headline(read: InboxRead): string {
  if (read.state === 'reading') return 'Reading your notifications…';
  if (read.state === 'failed') return 'Notifications could not be loaded.';
  return read.unread ? `${read.unread} unread` : 'Nothing new';
}

/** The row's link as the router takes it: the path, and its query as the search. */
function inboxTarget(deepLink: string): { to: '/'; search: never } {
  const { path, search } = linkParts(deepLink);
  return { to: path as '/', search: search as never };
}

function InboxList({
  items,
  onFollow,
}: {
  items: Notification[];
  onFollow: (n: Notification) => void;
}): ReactNode {
  const { ago } = useFormat();
  return (
    <ul className="bell__list">
      {items.slice(0, 30).map((n) => (
        <li key={n.id} className={n.readAt ? '' : 'is-unread'}>
          <Link {...inboxTarget(n.deepLink)} onClick={() => onFollow(n)}>
            <InboxRow notice={n} when={ago(n.createdAt)} />
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** One notice: its kind's mark, what happened, why and what to do in grey, then the job and when. */
function InboxRow({ notice, when }: { notice: Notification; when: string }): ReactNode {
  const { icon: Icon, tone } = noticeLook(notice.kind);
  const { line, more } = splitNotice(notice.subject);
  return (
    <>
      <span className={`bell__mark is-${tone}`} aria-hidden="true">
        <Icon size={16} />
      </span>
      <span className="bell__words">
        <span className="bell__subject">{line}</span>
        {more && <span className="bell__more">{more}</span>}
        <span className="bell__meta">
          {notice.jobName ? `${notice.jobName} · ` : ''}
          {when}
        </span>
      </span>
    </>
  );
}
